import type { BoundaryData } from '@/src/lib/api';
import {
  calculateCenterAndZoomFromBoundary,
  calculateCenterAndZoomFromBounds,
} from '@/src/components/features/search/Map/calculateCenterAndZoomFromBoundary';
import type { MapState } from '@/src/lib/hooks/map/mapStateKeys';
import type { MapPositionFromViewport } from '@/src/lib/map/viewportBounds';
import { SRP_LISTINGS_VIEW } from '../_data/srpListingsView';

export type MapStateFallbackBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

type BuildInitialSrpMapStateArgs = {
  boundary: BoundaryData | null;
  initialMapPosition: MapPositionFromViewport | null;
  fallbackBounds?: MapStateFallbackBounds;
};

function hasUsableFallbackBounds(
  fallbackBounds: MapStateFallbackBounds | undefined,
): fallbackBounds is MapStateFallbackBounds {
  return (
    fallbackBounds?.minLat != null &&
    fallbackBounds.maxLat != null &&
    fallbackBounds.minLon != null &&
    fallbackBounds.maxLon != null
  );
}

export function buildInitialSrpMapState({
  boundary,
  initialMapPosition,
  fallbackBounds,
}: BuildInitialSrpMapStateArgs): MapState | null {
  if (initialMapPosition) {
    return {
      bounds: initialMapPosition.bounds,
      center: initialMapPosition.center,
      zoom: initialMapPosition.zoom,
      source: 'url',
    };
  }

  const boundaryPosition = boundary?.coordinates
    ? calculateCenterAndZoomFromBoundary(
        boundary.coordinates,
        SRP_LISTINGS_VIEW.width,
        SRP_LISTINGS_VIEW.height,
        SRP_LISTINGS_VIEW.padding,
      )
    : null;

  const fallbackPosition =
    boundaryPosition ??
    (hasUsableFallbackBounds(fallbackBounds)
      ? calculateCenterAndZoomFromBounds(
          {
            minLat: fallbackBounds.minLat,
            maxLat: fallbackBounds.maxLat,
            minLng: fallbackBounds.minLon,
            maxLng: fallbackBounds.maxLon,
          },
          SRP_LISTINGS_VIEW.width,
          SRP_LISTINGS_VIEW.height,
          SRP_LISTINGS_VIEW.padding,
        )
      : null);

  if (!fallbackPosition) {
    return null;
  }

  return {
    bounds: fallbackPosition.bounds,
    center: fallbackPosition.center,
    zoom: fallbackPosition.zoom,
    source: 'boundary',
  };
}
